import { useState, useCallback, useEffect } from 'react';
import type { SavedRoute, SavedRoutesStorage, Waypoint } from '../lib/types';
import type { CostingModel } from '@/api/geo';
import { generateShareUrl } from '../lib/route-url';

const STORAGE_KEY = 'dsc-saved-routes';
const STORAGE_VERSION = 1;

function loadFromStorage(): SavedRoute[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];

    const parsed: SavedRoutesStorage = JSON.parse(data);
    if (parsed.version !== STORAGE_VERSION) {
      // Handle migration if needed
      return [];
    }
    return parsed.routes;
  } catch {
    return [];
  }
}

function saveToStorage(routes: SavedRoute[]): void {
  const data: SavedRoutesStorage = {
    routes,
    version: STORAGE_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function generateRouteId(): string {
  return `route-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useSavedRoutes() {
  const [routes, setRoutes] = useState<SavedRoute[]>(() => loadFromStorage());

  // Sync to localStorage when routes change
  useEffect(() => {
    saveToStorage(routes);
  }, [routes]);

  const saveRoute = useCallback(
    (name: string, waypoints: Waypoint[], costing: CostingModel): SavedRoute => {
      const now = Date.now();
      const newRoute: SavedRoute = {
        id: generateRouteId(),
        name,
        waypoints,
        costing,
        createdAt: now,
        updatedAt: now,
      };

      setRoutes((prev) => [newRoute, ...prev]);
      return newRoute;
    },
    []
  );

  const updateRoute = useCallback(
    (id: string, updates: Partial<Pick<SavedRoute, 'name' | 'waypoints' | 'costing'>>): void => {
      setRoutes((prev) =>
        prev.map((route) =>
          route.id === id
            ? { ...route, ...updates, updatedAt: Date.now() }
            : route
        )
      );
    },
    []
  );

  const deleteRoute = useCallback((id: string): void => {
    setRoutes((prev) => prev.filter((route) => route.id !== id));
  }, []);

  const getShareUrl = useCallback(
    (route: SavedRoute): string => {
      return generateShareUrl(route.waypoints, route.costing);
    },
    []
  );

  const duplicateRoute = useCallback(
    (route: SavedRoute): SavedRoute => {
      const now = Date.now();
      const newRoute: SavedRoute = {
        ...route,
        id: generateRouteId(),
        name: `${route.name} (копия)`,
        createdAt: now,
        updatedAt: now,
      };

      setRoutes((prev) => [newRoute, ...prev]);
      return newRoute;
    },
    []
  );

  return {
    routes,
    saveRoute,
    updateRoute,
    deleteRoute,
    getShareUrl,
    duplicateRoute,
  };
}
